"use client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";
import { useLocale } from "next-intl";
import React, { useRef } from "react";
import AnimatedElement from "./animated-element";

interface CarouselSectionProps<T> {
  title?: string;
  subTitle?: string;
  icon?: React.ReactNode;
  items: T[];
  renderItem: (item: T) => React.ReactNode;

  breakpoints?: {
    base?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  };
  className?: string;
  contentClassName?: string;
  displayArrows?: boolean;
}

function CarouselSection<T>({
  title,
  subTitle,
  icon,
  items,
  renderItem,
  breakpoints = {
    base: "basis-[50%]",
    sm: "sm:basis-[45%]",
    md: "md:basis-1/3",
    lg: "lg:basis-[19%]",
  },
  className,
  contentClassName,
  displayArrows = true,
}: CarouselSectionProps<T>) {
  const locale = useLocale();

  // Autoplay plugin ref
  const autoplayRef = useRef(
    Autoplay({
      delay: 5000, // autoplay speed
      stopOnMouseEnter: true,
      stopOnInteraction: true,
    }),
  );

  return (
    <section className={cn("relative", className)}>
      <div className={cn("sm:space-y-1", displayArrows && "mb-5")}>
        <div className="flex items-center gap-1">
          {icon && (
            <AnimatedElement
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: "easeInOut",
                duration: 0.5,
              }}
            >
              {icon}
            </AnimatedElement>
          )}
          {title && (
            <AnimatedElement
              className="title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: "easeInOut",
                duration: 0.5,
                delay: 0.2,
              }}
            >
              {title}
            </AnimatedElement>
          )}
        </div>
        {subTitle && (
          <AnimatedElement
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              ease: "easeInOut",
              duration: 0.5,
              delay: 0.4,
            }}
            className="subtitle text-muted-foreground sm:text-lg max-w-xs sm:max-w-full"
          >
            {subTitle}
          </AnimatedElement>
        )}
      </div>
      {displayArrows ? (
        <AnimatedElement
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            ease: "easeInOut",
            duration: 0.5,
            delay: 0.6,
          }}
        >
          <Carousel
            plugins={[autoplayRef.current]}
            opts={{
              dragFree: true,
              slidesToScroll: "auto",
            }}
          >
            <CarouselContent className={cn("py-3", contentClassName)}>
              {items.map((item, index) => (
                <CarouselItem
                  key={index}
                  className={cn(
                    breakpoints.base,
                    breakpoints.sm,
                    breakpoints.md,
                    breakpoints.lg,
                    breakpoints.xl,
                  )}
                >
                  {renderItem(item)}
                </CarouselItem>
              ))}
            </CarouselContent>

            {displayArrows && (
              <>
                <CarouselNext
                  className={cn(
                    "hover:bg-primary/90 hover:text-white -top-10 w-6.5 h-6.5 sm:w-8 sm:h-8",
                    locale === "en" ? "-right-1" : "-left-1",
                  )}
                />

                <CarouselPrevious
                  className={cn(
                    "hover:bg-primary/90 hover:text-white -top-10 w-6.5 h-6.5 sm:w-8 sm:h-8",
                    locale === "en"
                      ? "right-6.5 sm:right-9"
                      : "left-6.5 sm:left-9",
                  )}
                />
              </>
            )}
          </Carousel>
        </AnimatedElement>
      ) : (
        <Carousel
          plugins={[autoplayRef.current]}
          opts={{
            dragFree: true,
            slidesToScroll: "auto",
          }}
        >
          <CarouselContent className={cn("py-3", contentClassName)}>
            {items.map((item, index) => (
              <CarouselItem
                key={index}
                className={cn(
                  breakpoints.base,
                  breakpoints.sm,
                  breakpoints.md,
                  breakpoints.lg,
                  breakpoints.xl,
                )}
              >
                {renderItem(item)}
              </CarouselItem>
            ))}
          </CarouselContent>

          {displayArrows && (
            <>
              <CarouselNext
                className={cn(
                  "hover:bg-primary/90 hover:text-white -top-10 w-6.5 h-6.5 sm:w-8 sm:h-8",
                  locale === "en" ? "-right-1" : "-left-1",
                )}
              />

              <CarouselPrevious
                className={cn(
                  "hover:bg-primary/90 hover:text-white -top-10 w-6.5 h-6.5 sm:w-8 sm:h-8",
                  locale === "en"
                    ? "right-6.5 sm:right-9"
                    : "left-6.5 sm:left-9",
                )}
              />
            </>
          )}
        </Carousel>
      )}
    </section>
  );
}

export default CarouselSection;
